import React, {Dispatch, SetStateAction, useContext, useMemo, useState} from 'react'
import AppContext from '../../appContext'
import Dialog from '../../components/Dialog'
import ApplicationDataPanel from '../../components/ApplicationDataPanel'
import {SamplePoint} from '../../components/GeoMap'
import {AmendmentType, YearOption, SampleValue} from '../../components/ApplicationDataSidebar'
import InterpolationAlgorithmType from '../../controllers/InterpolationAlgorithmType'
import LimeProgramLabInputs from '../../controllers/LimeProgramLabInputs'
import LimePlayFieldRow from '../../controllers/LimePlayFieldRow'
import SampleDepth from '../../controllers/SampleDepth'

interface ApplicationDataDialogProps {
    show: boolean
    setShow: Dispatch<SetStateAction<boolean>>
    labInputs: LimeProgramLabInputs
    calculatedRows: LimePlayFieldRow[]
    boundaryCoordinates?: Array<{x: number; y: number}>
    boundaryLoading?: boolean
    boundaryError?: boolean
    landId?: number
    programId?: number
}

const values: Record<AmendmentType, Record<YearOption, (row: LimePlayFieldRow) => number | null>> = {
    lime: {
        need: row => row.amendmentNeedKgHa ?? null,
        y1: row => row.appliedYear1KgHa ?? null,
        y2: row => row.appliedYear2KgHa ?? null,
        y3: row => row.appliedYear3KgHa ?? null
    },
    gypsum: {
        need: row => row.gypsumNeedKgHa ?? null,
        y1: row => row.gypsumYear1KgHa ?? null,
        y2: row => row.gypsumYear2KgHa ?? null,
        y3: () => null // Gypsum doesn't have Y3
    },
    kFert: {
        need: row => row.kNeedKgHa ?? null,
        y1: row => row.kFertYear1KgHa ?? null,
        y2: row => row.kFertYear2KgHa ?? null,
        y3: () => null // K Fert doesn't have Y3
    },
    pFert: {
        need: row => row.pNeedKgHa ?? null,
        y1: row => row.pFertYear1KgHa ?? null,
        y2: row => row.pFertYear2KgHa ?? null,
        y3: () => null // P Fert doesn't have Y3
    }
};

/**
 * Get the value for a specific amendment type and year from a calculated row.
 */
function getAmendmentValue(
    row: LimePlayFieldRow,
    amendmentType: AmendmentType,
    yearOption: YearOption
): number | null {
    return values[amendmentType][yearOption](row);
}

const ApplicationDataDialog: React.FC<ApplicationDataDialogProps> = ({
    show,
    setShow,
    labInputs,
    calculatedRows,
    boundaryCoordinates,
    boundaryLoading,
    boundaryError,
    landId,
    programId
}) => {
    const app = useContext(AppContext)
    const [amendmentType, setAmendmentType] = useState<AmendmentType>('lime')
    const [yearOption, setYearOption] = useState<YearOption>('y1')
    const [algorithm, setAlgorithm] = useState<InterpolationAlgorithmType>(InterpolationAlgorithmType.Kriging)

    // Handle year option when amendment type changes
    // Reset to 'y1' if current year is 'y3' and new amendment doesn't support it
    const handleAmendmentChange = (type: AmendmentType) => {
        setAmendmentType(type)
        if (yearOption === 'y3' && type !== 'lime') {
            setYearOption('y1')
        }
    }

    // Subsoil samples are excluded from the interpolation map — only topsoil drives recommendations.
    // Transform labInputs to sample points (for map)
    const samplePoints: SamplePoint[] = useMemo(() => {
        return labInputs.rows
            .filter(row => row.sampleDepth !== SampleDepth.SubSoil)
            .filter(row => row.latitude !== null && row.longitude !== null)
            .map(row => ({
                latitude: row.latitude!,
                longitude: row.longitude!,
                label: row.referenceNr || row.landRef
            }))
    }, [labInputs])

    // Transform calculatedRows to sample values (for sidebar)
    const sampleValues: SampleValue[] = useMemo(() => {
        return calculatedRows
            .map((calcRow, index) => {
                const labRow = labInputs.rows[index]
                if (labRow?.sampleDepth === SampleDepth.SubSoil) return null
                const label = labRow?.referenceNr || labRow?.landRef || `Sample ${index + 1}`
                const value = getAmendmentValue(calcRow, amendmentType, yearOption)
                return {label, value}
            })
            .filter((v): v is SampleValue => v !== null)
    }, [calculatedRows, labInputs, amendmentType, yearOption])

    return (
        <Dialog
            title={app.word('qc_map')}
            show={show}
            setShow={setShow}
            body={
                <div className="p-4 min-w-[70vw]">
                    {boundaryLoading ? (
                        <div className="flex items-center justify-center h-[60vh] bg-gray-100 rounded">
                            <div className="text-gray-500">{app.word('loading')}...</div>
                        </div>
                    ) : (
                        <ApplicationDataPanel
                            samplePoints={samplePoints}
                            sampleValues={sampleValues}
                            boundaryCoordinates={boundaryCoordinates}
                            amendmentType={amendmentType}
                            yearOption={yearOption}
                            algorithm={algorithm}
                            onAmendmentChange={handleAmendmentChange}
                            onYearChange={setYearOption}
                            onAlgorithmChange={setAlgorithm}
                            boundaryError={boundaryError}
                            landId={landId}
                            programId={programId}
                        />
                    )}
                    <div className="flex justify-end">
                        <button
                            className="btn-sm bg-primary"
                            onClick={() => setShow(false)}
                        >
                            {app.word('done')}
                        </button>
                    </div>
                </div>
            }
        />
    )
}

export default ApplicationDataDialog
